import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from "react-router-dom";
import App from './App';
import SplashScreen from './SplashScreen';
import { AppProvider } from './context/AppContext';
import './utils/disableConsole'; // Désactive les console.log en production

// Vérification de Telegram au démarrage
if (window.Telegram && window.Telegram.WebApp) {
  console.log("Telegram WebApp API détectée lors du chargement initial");
  
  // Vérifier si des données d'utilisateur sont disponibles
  if (window.Telegram.WebApp.initDataUnsafe && window.Telegram.WebApp.initDataUnsafe.user) {
    console.log("Données utilisateur Telegram disponibles");
  } else {
    console.warn("Aucune donnée utilisateur Telegram trouvée");
  }
} else {
  console.warn("Telegram WebApp API non détectée au chargement");
}

// Nettoyage de sessionStorage pour la gestion des redirections
sessionStorage.removeItem('initialRedirectionDone');

// Gestion des erreurs à l'échelle globale
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Mettre à jour l'état pour le prochain rendu
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Vous pouvez aussi enregistrer l'erreur
    console.error("Composant avec erreur:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // Afficher une interface de secours
      return (
        <div className="min-h-screen bg-gray-50 p-6 flex flex-col items-center justify-center">
          <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-red-600 mb-4">Une erreur est survenue</h2>
            <p className="text-gray-700 mb-4">
              {this.state.error && this.state.error.toString()}
            </p>
            <div className="mt-4">
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg"
              >
                Rafraîchir la page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Interface utilisateur minimaliste en cas d'erreur
const MinimalUI = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <header className="bg-white shadow-sm rounded-lg p-4 mb-6">
        <h1 className="text-xl font-bold">Kapella App</h1>
      </header>
      
      <main className="bg-white shadow-sm rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Tableau de bord</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-600 text-white p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Balance</h3>
            <p className="text-2xl font-bold">10,000 FCFA</p>
          </div>
          
          <div className="bg-gray-100 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Abonnements</h3>
            <p className="text-2xl font-bold">0</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <button 
            onClick={() => navigate('/ProductsList')}
            className="w-full bg-gray-100 p-4 rounded-lg text-left font-medium flex items-center justify-between"
          >
            <span>Produits</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          <button 
            onClick={() => navigate('/GroupeListe')}
            className="w-full bg-gray-100 p-4 rounded-lg text-left font-medium flex items-center justify-between"
          >
            <span>Groupes</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          <button 
            onClick={() => navigate('/Statistics')}
            className="w-full bg-gray-100 p-4 rounded-lg text-left font-medium flex items-center justify-between"
          >
            <span>Statistiques</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </main>
    </div>
  );
};

// Application avec deux modes possibles : complet ou minimal
const AppWrapper = () => {
  const [useMinimalUI, setUseMinimalUI] = useState(false);
  
  // Essayer le mode complet, mais avoir MinimalUI comme fallback
  return (
    <ErrorBoundary
      onError={() => {
        console.log("Échec du mode complet, passage au mode minimal");
        setUseMinimalUI(true);
      }}
    >
      {useMinimalUI ? (
        <Routes>
          <Route path="/*" element={<MinimalUI />} />
        </Routes>
      ) : (
        <AppProvider>
          {/* Le SplashScreen est géré par AppContext */}
          <SplashScreen />
          <App />
        </AppProvider>
      )}
    </ErrorBoundary>
  );
};

// Rendu de l'application
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Router>
    <ErrorBoundary>
      <AppWrapper />
    </ErrorBoundary>
  </Router>
);